// Verb Groups — collapsible cards grouping every verb by conjugation pattern
// (regular, the stem-change families, eu-only and fully irregular), each
// with a visual diagram. Ported from the legacy views.groups with three
// transformations: window.PORTUGUESE_DATA.* → direct PT imports, inline
// onclick="navigate(...)" → data-inf + addEventListener, and the global `app`
// → a mount-element parameter.
import { ALL_VERBS, IRREGULAR_FORMS, conjugate } from "../data/index.js";
import { navigate } from "../router/router.js";

export function groupsView(app) {
  const regulars = ALL_VERBS.filter((v) => v.group === "regular");
  const irregulars = ALL_VERBS.filter((v) => v.group === "irregular");
  const stemChange = ALL_VERBS.filter((v) => v.group === "stem-change");

  // Sub-categorize irregulars: eu-only verbs change only in the first-person
  // singular of the present tense.
  const euOnly = irregulars.filter((v) => {
    const irr = IRREGULAR_FORMS[v.inf];
    if (!irr || !irr.present) return false;
    const reg = conjugate(v.inf, "present").regular;
    const flags = irr.present.map((f, i) => f !== reg[i]);
    return flags[0] && !flags[1] && !flags[2] && !flags[3] && !flags[4] && !flags[5];
  });
  const fullyIrregular = irregulars.filter((v) => !euOnly.includes(v));

  // Sub-categorize stem-changes by the vowel shift seen in the eu form.
  function stemPattern(verb) {
    const irr = IRREGULAR_FORMS[verb.inf];
    if (!irr || !irr.present) return "other";
    const eu = irr.present[0];
    if (eu.includes("ie")) return "e→ie";
    if (eu.includes("ue")) return "o→ue";
    if (verb.inf.includes("e") && eu.match(/i[^e]/)) return "e→i";
    return "other";
  }
  const eToIe = stemChange.filter((v) => stemPattern(v) === "e→ie");
  const oToUe = stemChange.filter((v) => stemPattern(v) === "o→ue");
  const eToI = stemChange.filter((v) => stemPattern(v) === "e→i");
  const otherStem = stemChange.filter(
    (v) => !eToIe.includes(v) && !oToUe.includes(v) && !eToI.includes(v)
  );

  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Verb Groups</h2>
      <p class="text-sage-600 text-sm mb-6">Portuguese verbs fall into patterns. Master the patterns and you've mastered hundreds of verbs at once.</p>

      <div class="space-y-4" id="groups-container"></div>
    </section>
  `;

  const container = app.querySelector("#groups-container");

  // ---- Visual diagrams for each group ----

  // Boot diagram: highlights the four "changed" cells (eu, tu, ele, eles)
  // around the unchanged nós/vós (the "heel" of the boot).
  function bootDiagram(title, regularExample, changedForms) {
    return `
      <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">${title} — present tense</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="relative">
            <svg width="0" height="0" style="position:absolute"></svg>
            <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0">${changedForms.eu}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200">${changedForms.nos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.tu}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200 border-t-0">${changedForms.vos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.ele}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-r-lg border-2 border-red-300 border-l-0 border-t-0">${changedForms.eles}</div>
            </div>
            <p class="text-xs text-sage-400 mt-2 text-center">👢 the "boot" shape</p>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-red-300 rounded-sm align-middle"></span> <b>Red cells change</b> — the stem shifts here.</p>
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-sage-200 rounded-sm align-middle"></span> <b>Grey cells stay regular</b> — nós and vós keep the plain stem.</p>
            <p class="mt-2 italic">Example: <b>${regularExample}</b></p>
          </div>
        </div>
      </div>
    `;
  }

  function regularDiagram() {
    return `
      <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The three patterns — present tense</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-AR · falar</p>
            <p class="text-xs text-sage-400 mb-2">to speak</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>eu fal<span class="text-sage-900 font-bold">o</span></div>
              <div>tu fal<span class="text-sage-900 font-bold">as</span></div>
              <div>ele fal<span class="text-sage-900 font-bold">a</span></div>
              <div>nós fal<span class="text-sage-900 font-bold">amos</span></div>
              <div>vós fal<span class="text-sage-900 font-bold">ais</span></div>
              <div>eles fal<span class="text-sage-900 font-bold">am</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-ER · comer</p>
            <p class="text-xs text-sage-400 mb-2">to eat</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>eu com<span class="text-sage-900 font-bold">o</span></div>
              <div>tu com<span class="text-sage-900 font-bold">es</span></div>
              <div>ele com<span class="text-sage-900 font-bold">e</span></div>
              <div>nós com<span class="text-sage-900 font-bold">emos</span></div>
              <div>vós com<span class="text-sage-900 font-bold">eis</span></div>
              <div>eles com<span class="text-sage-900 font-bold">em</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-IR · partir</p>
            <p class="text-xs text-sage-400 mb-2">to leave</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>eu part<span class="text-sage-900 font-bold">o</span></div>
              <div>tu part<span class="text-sage-900 font-bold">es</span></div>
              <div>ele part<span class="text-sage-900 font-bold">e</span></div>
              <div>nós part<span class="text-sage-900 font-bold">imos</span></div>
              <div>vós part<span class="text-sage-900 font-bold">is</span></div>
              <div>eles part<span class="text-sage-900 font-bold">em</span></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 -ER and -IR are identical except for nós (-emos vs -imos) and vós (-eis vs -is).</p>
      </div>
    `;
  }

  function euOnlyDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">Only the "eu" form changes</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
            <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg border-2 border-red-300 font-bold">pe<span class="underline">ço</span></div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">pedimos</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">pedes</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">pedis</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">pede</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">pedem</div>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p>The eu-only group: <b>pedir, ouvir, perder, fazer, dizer, trazer</b>. Memorize as a chant:</p>
            <p class="mt-2 font-mono text-sage-800">peço · ouço · perco<br>faço · digo · trago</p>
            <p class="mt-2 text-sage-400 italic">* ter, pôr and vir also change beyond the eu form (tens/tem, pões/põe, vens/vem); the pure eu-only verbs like pedir/ouvir don't.</p>
          </div>
        </div>
      </div>
    `;
  }

  function fullyIrregularDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The big four — must be memorized</p>
        <div class="grid sm:grid-cols-2 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">ser <span class="text-sage-400 font-normal text-xs">— to be (essence)</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>eu <b class="text-red-700">sou</b></div>
              <div>tu <b class="text-red-700">és</b></div>
              <div>ele <b class="text-red-700">é</b></div>
              <div>nós <b class="text-red-700">somos</b></div>
              <div>vós <b class="text-red-700">sois</b></div>
              <div>eles <b class="text-red-700">são</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">estar <span class="text-sage-400 font-normal text-xs">— to be (state)</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>eu <b class="text-red-700">estou</b></div>
              <div>tu estás</div>
              <div>ele está</div>
              <div>nós estamos</div>
              <div>vós estais</div>
              <div>eles estão</div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">ir <span class="text-sage-400 font-normal text-xs">— to go</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>eu <b class="text-red-700">vou</b></div>
              <div>tu <b class="text-red-700">vais</b></div>
              <div>ele <b class="text-red-700">vai</b></div>
              <div>nós <b class="text-red-700">vamos</b></div>
              <div>vós <b class="text-red-700">ides</b></div>
              <div>eles <b class="text-red-700">vão</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">ter <span class="text-sage-400 font-normal text-xs">— to have</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>eu <b class="text-red-700">tenho</b></div>
              <div>tu <b class="text-red-700">tens</b></div>
              <div>ele <b class="text-red-700">tem</b></div>
              <div>nós temos</div>
              <div>vós tendes</div>
              <div>eles <b class="text-red-700">têm</b></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 Fun fact: <b>ser</b> and <b>ir</b> are identical in the preterite (fui, foste, foi…). Context decides.</p>
      </div>
    `;
  }

  function renderGroup(title, color, description, verbs, infographic) {
    const sec = document.createElement("div");
    sec.className = "bg-white rounded-2xl border border-sage-100 p-5";
    sec.innerHTML = `
      <div class="flex items-center justify-between mb-2 cursor-pointer group-header">
        <div>
          <h3 class="font-bold text-lg text-sage-800">${title} <span class="text-sage-400 text-sm font-normal">(${verbs.length})</span></h3>
          <p class="text-sm text-sage-600 mt-1">${description}</p>
        </div>
        <span class="text-sage-400 text-xl group-arrow transition-transform">›</span>
      </div>
      <div class="group-body hidden mt-3">
        ${infographic || ""}
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2 mt-1">Verbs in this group</p>
        <div class="flex flex-wrap gap-1.5">
          ${verbs
            .slice(0, 80)
            .map(
              (v) => `
            <button data-inf="${v.inf}"
              class="verb-chip text-xs px-2.5 py-1 rounded-full ${color} hover:opacity-80 transition">
              ${v.emoji ? v.emoji + " " : ""}${v.inf}
            </button>
          `
            )
            .join("")}
          ${
            verbs.length > 80
              ? `<span class="text-xs text-sage-400 px-2 py-1">+${verbs.length - 80} more</span>`
              : ""
          }
        </div>
      </div>
    `;
    sec.querySelector(".group-header").addEventListener("click", () => {
      const body = sec.querySelector(".group-body");
      const arrow = sec.querySelector(".group-arrow");
      body.classList.toggle("hidden");
      arrow.style.transform = body.classList.contains("hidden") ? "" : "rotate(90deg)";
    });
    sec.querySelectorAll(".verb-chip").forEach((btn) =>
      btn.addEventListener("click", () =>
        navigate("verb-detail", { inf: btn.dataset.inf })
      )
    );
    container.appendChild(sec);
  }

  renderGroup(
    "Regular verbs",
    "bg-sage-100 text-sage-700",
    "These follow the textbook -ar / -er / -ir patterns with no surprises. Once you know falar, comer, and partir, you can conjugate them all.",
    regulars,
    regularDiagram()
  );
  renderGroup(
    "Stem-changing: -ear (→ ei)",
    "bg-sand-100 text-sand-700",
    "Verbs ending -ear add an i (ei) in every form except nós and vós. Examples: passear → passeio, pentear → penteio.",
    eToIe,
    bootDiagram(
      "passear (to stroll)",
      "pentear → penteio, recear → receio, cear → ceio",
      { eu: "passeio", tu: "passeias", ele: "passeia", nos: "passeamos", vos: "passeais", eles: "passeiam" }
    )
  );
  renderGroup(
    "Stem-changing: -iar (odiar group)",
    "bg-sand-100 text-sand-700",
    "A small group of -iar verbs take ei across the boot. Examples: odiar → odeio, ansiar → anseio.",
    oToUe,
    bootDiagram(
      "odiar (to hate)",
      "ansiar → anseio, incendiar → incendeio, mediar → medeio",
      { eu: "odeio", tu: "odeias", ele: "odeia", nos: "odiamos", vos: "odiais", eles: "odeiam" }
    )
  );
  renderGroup(
    "Stem-changing: e → i (eu form)",
    "bg-sand-100 text-sand-700",
    "In many -ir verbs the stem 'e' becomes 'i' in the eu form only. Examples: servir → sirvo, sentir → sinto, repetir → repito.",
    eToI,
    bootDiagram(
      "servir (to serve) — only the eu form changes",
      "sentir → sinto, repetir → repito, seguir → sigo",
      { eu: "sirvo", tu: "serves", ele: "serve", nos: "servimos", vos: "servis", eles: "servem" }
    )
  );
  if (otherStem.length)
    renderGroup(
      "Other irregular stems",
      "bg-sand-100 text-sand-700",
      "Less common patterns (o→u raisers, -uir verbs, etc).",
      otherStem,
      bootDiagram(
        "construir (to build)",
        "destruir → destrói, dormir → durmo (o→u, eu only)",
        { eu: "construo", tu: "constróis", ele: "constrói", nos: "construímos", vos: "construís", eles: "constroem" }
      )
    );
  renderGroup(
    "Eu-only irregulars",
    "bg-red-100 text-red-700",
    "These verbs are completely regular EXCEPT in the 'eu' (I) form of the present tense. Examples: fazer → faço, pedir → peço, ouvir → ouço.",
    euOnly,
    euOnlyDiagram()
  );
  renderGroup(
    "Fully irregular",
    "bg-red-100 text-red-700",
    "These verbs deviate across multiple forms and tenses. The big ones — ser, estar, ir, ter — must simply be memorized.",
    fullyIrregular,
    fullyIrregularDiagram()
  );

  window.scrollTo({ top: 0, behavior: "smooth" });
}
